import { Box, Radio, Stack, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { RHFDatePicker, RHFSelect, RHFTextField, RHFTimePicker } from '@Components/hook-form';

// Styles
import Styles from './CreateRaindropContent.module.scss';
import { useGetProvidersLazyQuery, useGetProviderConfigsLazyQuery } from '@Src/generated.graphql';
import { useEffect, useMemo } from 'react';
import { MenuItemEx } from '@Components/shared/selects/MenuItemEx';
import { useFormContext, Controller } from 'react-hook-form';
import { TextField } from '@mui/material';
import type { CreateRaindropFormState } from '@Forms/raindrop/CreateRaindropFormContext';
import {
  useManageCreateRaindropSchema,
  createRaindropSchema,
} from '@Forms/raindrop/CreateRaindropFormContext';
import { useAuthorization } from '@Modal/hooks/authorization/useAuthorization';

const MS_IN_DAY = 1000 * 60 * 60 * 24;

const toNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const getInclusiveDayDiff = (startDate: unknown, endDate: unknown): number => {
  if (!startDate || !endDate) {
    return 0;
  }

  const start = new Date(startDate as string | number | Date);
  const end = new Date(endDate as string | number | Date);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 0;
  }

  const startUtc = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const endUtc = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  const diff = Math.floor((endUtc - startUtc) / MS_IN_DAY);

  if (diff < 0) {
    return 0;
  }

  return diff + 1;
};

export const CreateRaindropContent = () => {
  const { setValue, getValues, watch, trigger, control } =
    useFormContext<CreateRaindropFormState>();
  const { currentUser } = useAuthorization();

  const showCasinoId = watch('showCasinoId');
  const operatorId = watch('providerId');
  const timezone = watch('timezone');
  const isFormDisabled = showCasinoId === null;
  const currency = watch('currency');
  const freebetValue = watch('freebetValue');
  const freebetsPerRain = watch('freebetsPerRain');
  const rainsPerDay = watch('rainsPerDay');
  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const distributionFrom = watch('distributionFrom');
  const distributionTo = watch('distributionTo');
  const playerMinWager = watch('playerMinWager');
  const wagerPeriod = watch('wagerPeriod');

  const [getProviders, { data }] = useGetProvidersLazyQuery({
    onCompleted: (response) => {
      const providers = response?.providers ?? [];
      if (providers.length !== 1) {
        return;
      }

      const singleProviderId = providers[0]?.id;
      const currentOperator = getValues('providerId');

      if (singleProviderId && !currentOperator) {
        setValue('providerId', singleProviderId, { shouldValidate: true, shouldDirty: false });
      }
    },
  });

  const [getProviderConfigs, { data: providerConfigs }] = useGetProviderConfigsLazyQuery();
  const { setSchema } = useManageCreateRaindropSchema();

  useEffect(() => {
    if (currentUser?.isSuper === true) {
      getProviders();
    }
  }, [currentUser?.isSuper, getProviders]);

  useEffect(() => {
    if (currentUser?.isSuper !== true && currentUser?.providerIds) {
      const providerIds = currentUser.providerIds;
      if (providerIds.length === 1) {
        const singleProviderId = providerIds[0];
        const currentOperator = getValues('providerId');

        if (singleProviderId && !currentOperator) {
          setValue('providerId', singleProviderId, { shouldValidate: true, shouldDirty: false });
        }
      }
    }
  }, [currentUser?.isSuper, currentUser?.providerIds, getValues, setValue]);

  const formatOffsetLabel = (minutes: number): string => {
    const sign = minutes >= 0 ? '+' : '-';
    const absolute = Math.abs(minutes);
    const hours = Math.floor(absolute / 60)
      .toString()
      .padStart(2, '0');
    const mins = (absolute % 60).toString().padStart(2, '0');
    return `UTC${sign}${hours}:${mins}`;
  };

  const timezoneOptions = useMemo(() => {
    const options: string[] = [];
    for (let offset = -720; offset <= 840; offset += 30) {
      options.push(formatOffsetLabel(offset));
    }
    return options;
  }, []);

  const minDate = useMemo(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }, []);

  const freebetValidityMinDate = useMemo(() => {
    if (endDate) {
      const end = new Date(endDate);
      const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      const nextDay = new Date(endDateOnly);
      nextDay.setDate(nextDay.getDate() + 1);
      return nextDay;
    }
    return minDate;
  }, [endDate, minDate]);

  useEffect(() => {
    if (timezone) {
      return;
    }

    const localOffsetMinutes = -new Date().getTimezoneOffset();
    const localTimezoneLabel = formatOffsetLabel(localOffsetMinutes);
    setValue('timezone', localTimezoneLabel, { shouldDirty: false, shouldValidate: true });
  }, [setValue, timezone]);

  const currencyOptions = useMemo(() => {
    const options = providerConfigs?.providerConfigs ?? [];
    if (!options.length) {
      return [] as string[];
    }

    const unique = new Set<string>();
    options.forEach(({ currency }) => {
      if (currency) {
        unique.add(currency);
      }
    });

    return Array.from(unique);
  }, [providerConfigs?.providerConfigs]);

  useEffect(() => {
    if (!operatorId) {
      setValue('currency', '', { shouldDirty: false, shouldValidate: false });
      return;
    }

    getProviderConfigs({ variables: { id: operatorId } });
  }, [getProviderConfigs, operatorId, setValue]);

  useEffect(() => {
    const configs = providerConfigs?.providerConfigs ?? [];
    const mappedConfigs = configs.map((config) => ({
      minBet: config.minBet,
      maxBet: config.maxBet,
      minAutoCashout: config.minAutoCashout ?? null,
      maxAutoCashout: config.maxAutoCashout ?? null,
      currency: config.currency,
      isRoundable: config.isRoundable,
    }));
    const newSchema = createRaindropSchema(mappedConfigs);
    setSchema(newSchema);
    const currentFreebetValue = getValues('freebetValue');
    if (currentFreebetValue !== null && currentFreebetValue !== undefined) {
      trigger('freebetValue');
    }
    const currentMinMultiplier = getValues('minMultiplier');
    if (currentMinMultiplier !== null && currentMinMultiplier !== undefined) {
      trigger('minMultiplier');
    }
  }, [providerConfigs?.providerConfigs, setSchema, trigger, getValues]);

  useEffect(() => {
    if (!operatorId) {
      return;
    }

    if (!currencyOptions.length) {
      setValue('currency', '', { shouldDirty: false, shouldValidate: false });
      return;
    }

    if (currencyOptions.length === 1) {
      const [onlyCurrency] = currencyOptions;
      if (getValues('currency') !== onlyCurrency) {
        setValue('currency', onlyCurrency, { shouldDirty: false, shouldValidate: true });
      }
    }
  }, [currencyOptions, getValues, operatorId, setValue]);

  useEffect(() => {
    const currentFreebetValue = getValues('freebetValue');
    if (currentFreebetValue !== null && currentFreebetValue !== undefined) {
      trigger('freebetValue');
    }
    const currentMinMultiplier = getValues('minMultiplier');
    if (currentMinMultiplier !== null && currentMinMultiplier !== undefined) {
      trigger('minMultiplier');
    }
  }, [operatorId, currency, trigger, getValues]);

  useEffect(() => {
    const parsedFreebetValue = toNumber(freebetValue);
    const parsedFreebetsPerRain = toNumber(freebetsPerRain);
    const parsedRainsPerDay = toNumber(rainsPerDay);

    const computedDailyBudget = parsedFreebetValue * parsedFreebetsPerRain * parsedRainsPerDay || 0;

    const days = getInclusiveDayDiff(startDate, endDate);
    const computedTotalBudget = computedDailyBudget * days || 0;

    const currentDailyBudget = toNumber(getValues('dayBudget'));
    if (currentDailyBudget !== computedDailyBudget) {
      setValue('dayBudget', computedDailyBudget, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }

    const currentTotalBudget = toNumber(getValues('totalBudget'));
    if (currentTotalBudget !== computedTotalBudget) {
      setValue('totalBudget', computedTotalBudget, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [endDate, freebetValue, getValues, freebetsPerRain, rainsPerDay, setValue, startDate]);

  useEffect(() => {
    if (distributionFrom && distributionTo && rainsPerDay) {
      trigger('distributionFrom');
      trigger('distributionTo');
      trigger('rainsPerDay');
    }
  }, [distributionFrom, distributionTo, rainsPerDay, trigger]);

  useEffect(() => {
    if (distributionFrom && distributionTo) {
      trigger('distributionFrom');
      trigger('distributionTo');
    }
  }, [startDate, endDate, distributionFrom, distributionTo, trigger]);

  useEffect(() => {
    if (endDate) {
      trigger('endDate');
    }
  }, [startDate, endDate, trigger]);

  useEffect(() => {
    const freebetValidityDate = getValues('freebetValidityDate');
    if (freebetValidityDate) {
      trigger('freebetValidityDate');
    }
  }, [endDate, getValues, trigger]);

  useEffect(() => {
    const isEmpty = (val: unknown) => {
      if (val === null || val === undefined) return true;
      if (typeof val === 'string' && val.trim() === '') return true;
      if (typeof val === 'number') {
        if (val === 0) return true;
        if (Number.isNaN(val)) return true;
      }
      return false;
    };

    const isPlayerMinWagerEmpty = isEmpty(playerMinWager);
    const isWagerPeriodEmpty = isEmpty(wagerPeriod);

    if (isPlayerMinWagerEmpty && isWagerPeriodEmpty) {
      trigger('playerMinWager');
      trigger('wagerPeriod');
      return;
    }

    if (!isPlayerMinWagerEmpty) {
      trigger('wagerPeriod');
    }
    if (!isWagerPeriodEmpty) {
      trigger('playerMinWager');
    }
  }, [playerMinWager, wagerPeriod, trigger]);

  return (
    <Stack className={Styles.CreateRaindropContent}>
      <Stack className={Styles.RadioColumn}>
        <Stack
          className={Styles.RadioOption}
          onClick={() => {
            setValue('showCasinoId', false, { shouldValidate: false });
          }}
          role="button"
          tabIndex={0}
        >
          <Radio
            checked={showCasinoId === false}
            value="operator"
            onChange={() => {
              setValue('showCasinoId', false, { shouldValidate: false });
            }}
          />
          <Stack direction="column">
            <strong>Send campaign to all casinos under this provider</strong>
            <Typography variant="body2" color="text.secondary">
              The campaign applies to every casino registered under the selected provider.
            </Typography>
          </Stack>
        </Stack>
        <Stack
          className={Styles.RadioOption}
          onClick={() => {
            setValue('showCasinoId', true, { shouldValidate: false });
          }}
          role="button"
          tabIndex={0}
        >
          <Radio
            checked={showCasinoId === true}
            value="multi"
            onChange={() => {
              setValue('showCasinoId', true, { shouldValidate: false });
            }}
          />
          <Stack direction="column">
            <strong>Send campaign to a specific casino</strong>
            <Typography variant="body2" color="text.secondary">
              The campaign applies to a single Casino ID under the selected provider.
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <strong>Setup Campaign</strong>
      <Stack className={Styles.Block}>
        <Tooltip
          title="Enter a clear, unique name for this campaign. This name will be used for tracking, reporting, and identifying the campaign in the list."
          placement="top"
        >
          <Box className={Styles.Input}>
            <RHFTextField name="name" label="Name*" disabled={isFormDisabled} />
          </Box>
        </Tooltip>
        <Tooltip
          title="Select the provider you want to run this campaign with. Only pre-integrated providers appear here, and the campaign will use their existing configuration."
          placement="top"
        >
          <Box className={Styles.Input}>
            <RHFSelect
              label="Provider"
              name="providerId"
              variant="outlined"
              disabled={
                isFormDisabled ||
                (currentUser?.isSuper === true && (data?.providers?.length ?? 0) === 1) ||
                (currentUser?.isSuper !== true && (currentUser?.providerIds?.length ?? 0) === 1)
              }
            >
              {currentUser?.isSuper === true
                ? data?.providers?.map((provider) => (
                    <MenuItemEx key={provider.id} value={provider.id} name="Providers">
                      {provider.name}
                    </MenuItemEx>
                  ))
                : currentUser?.providerIds?.map((providerId) => (
                    <MenuItemEx key={providerId} value={providerId} name="Providers">
                      {providerId}
                    </MenuItemEx>
                  ))}
            </RHFSelect>
          </Box>
        </Tooltip>
        {showCasinoId === true && (
          <Tooltip
            title="Choose how the campaign should be targeted:
            “Select casinos” lets you enter specific Casino IDs.
            “All casinos” sends the campaign to every connected casino."
            placement="top"
          >
            <Box className={Styles.Input}>
              <RHFTextField name="casinoId" label="Casino ID*" disabled={isFormDisabled} />
            </Box>
          </Tooltip>
        )}
      </Stack>
      <strong>Setup Raindrop</strong>
      <Stack className={Styles.Block}>
        <Tooltip
          title="Set the start and end dates for the campaign. Campaigns may run for a maximum of 1 month."
          placement="top"
        >
          <Box className={Styles.Input}>
            <RHFDatePicker
              name="startDate"
              label="Start Date*"
              status={isFormDisabled}
              minDate={minDate}
            />
          </Box>
        </Tooltip>
        <Tooltip
          title="Set the start and end dates for the campaign. Campaigns may run for a maximum of 1 month."
          placement="top"
        >
          <Box className={Styles.Input}>
            <RHFDatePicker
              name="endDate"
              label="End Date*"
              status={isFormDisabled}
              minDate={minDate}
            />
          </Box>
        </Tooltip>
        <Stack className={Styles.Block}>
          <Tooltip title="Select the timezone used for the distribution window." placement="top">
            <Box className={Styles.Input}>
              <RHFSelect
                label="Timezone"
                name="timezone"
                variant="outlined"
                disabled={isFormDisabled}
              >
                {timezoneOptions.map((option) => (
                  <MenuItemEx key={option} value={option} name="Timezone">
                    {option}
                  </MenuItemEx>
                ))}
              </RHFSelect>
            </Box>
          </Tooltip>
          <Tooltip
            title="Set the daily start in which rain events can be triggered. Select the earliest time of day when rain events can start. User 24h format"
            placement="top"
          >
            <Box className={Styles.Input}>
              <RHFTimePicker
                name="distributionFrom"
                label="Distribution From*"
                status={isFormDisabled}
              />
            </Box>
          </Tooltip>
          <Tooltip
            title="Set the daily start in which rain events can be triggered. Select the latest time of day when rain events can occur. User 24h format"
            placement="top"
          >
            <Box className={Styles.Input}>
              <RHFTimePicker
                name="distributionTo"
                label="Distribution To*"
                status={isFormDisabled}
              />
            </Box>
          </Tooltip>
        </Stack>
        <Tooltip
          title="Set how many freebets are included in each rain distribution. The campaign may have multiple distributions, and this number applies to each one."
          placement="top"
        >
          <Box className={Styles.Input}>
            <RHFTextField
              name="freebetsPerRain"
              label="Number of Freebets Per Rain*"
              disabled={isFormDisabled}
            />
          </Box>
        </Tooltip>
        <Tooltip
          title="Set how many rain events will occur per day. Each rain event triggers one distribution of freebets."
          placement="top"
        >
          <Box className={Styles.Input}>
            <RHFTextField
              name="rainsPerDay"
              label="Rain Counts Per Day*"
              disabled={isFormDisabled}
            />
          </Box>
        </Tooltip>
        <Tooltip
          title="Select the currency in which this campaign will operate. All freebet values and limits will use this currency."
          placement="top"
        >
          <Box className={Styles.Input}>
            <RHFSelect
              label="Currency"
              name="currency"
              variant="outlined"
              disabled={isFormDisabled}
            >
              {providerConfigs?.providerConfigs?.map((providerConfig) => (
                <MenuItemEx
                  key={providerConfig.id}
                  value={providerConfig.currency}
                  name="Currencies"
                >
                  {providerConfig.currency}
                </MenuItemEx>
              ))}
            </RHFSelect>
          </Box>
        </Tooltip>
        <Tooltip title="Set the value of each freebet" placement="top">
          <Box className={Styles.Input}>
            <RHFTextField name="freebetValue" label="Freebet Value*" disabled={isFormDisabled} />
          </Box>
        </Tooltip>
        <Tooltip
          title="Set the maximum number of freebets a user can claim during the time defined in the Claim Limitation Period"
          placement="top"
        >
          <Box className={Styles.Input}>
            <RHFTextField
              name="claimLimitNumber"
              label="Claim Limitation Number*"
              disabled={isFormDisabled}
            />
          </Box>
        </Tooltip>
        <Tooltip
          title="Set how long a user must wait before claiming the next freebet. “Rain duration” means the limitation applies for the entire campaign; other options (5s, 10s, 30s, 1m, 5m) apply per claim."
          placement="top"
        >
          <Box className={Styles.Input}>
            <RHFSelect
              label="Claim Limitation Period*"
              name="claimLimitPeriod"
              variant="outlined"
              disabled={isFormDisabled}
            >
              <MenuItemEx value="RAIN_DURATION" name="Claim Limitation Period">
                Rain duration
              </MenuItemEx>
              <MenuItemEx value="FIVE_SECONDS" name="Claim Limitation Period">
                5s
              </MenuItemEx>
              <MenuItemEx value="TEN_SECONDS" name="Claim Limitation Period">
                10s
              </MenuItemEx>
              <MenuItemEx value="THIRTY_SECONDS" name="Claim Limitation Period">
                30s
              </MenuItemEx>
              <MenuItemEx value="ONE_MINUTE" name="Claim Limitation Period">
                1m
              </MenuItemEx>
              <MenuItemEx value="FIVE_MINUTES" name="Claim Limitation Period">
                5m
              </MenuItemEx>
            </RHFSelect>
          </Box>
        </Tooltip>
        <Tooltip
          title="Set how long each rain distribution remains claimable. After this time (in minutes) expires, the distribution disappears from the player’s UI."
          placement="top"
        >
          <Box className={Styles.Input}>
            <RHFSelect
              label="Distribution Duration*"
              name="claimDeadlineMinutes"
              variant="outlined"
              disabled={isFormDisabled}
            >
              <MenuItemEx value="1" name="Claim Limitation Period">
                1m
              </MenuItemEx>
              <MenuItemEx value="2" name="Claim Limitation Period">
                2m
              </MenuItemEx>
              <MenuItemEx value="3" name="Claim Limitation Period">
                3m
              </MenuItemEx>
              <MenuItemEx value="5" name="Claim Limitation Period">
                5m
              </MenuItemEx>
            </RHFSelect>
          </Box>
        </Tooltip>
        <Tooltip
          title="Set the expiration date for claimed freebets. Any freebet not used by this date will automatically expire."
          placement="top"
        >
          <Box className={Styles.Input}>
            <RHFDatePicker
              name="freebetValidityDate"
              label="Freebet Validity Date"
              status={isFormDisabled}
              minDate={freebetValidityMinDate}
            />
          </Box>
        </Tooltip>
      </Stack>
      <Tooltip
        title="Set the minimum multiplier at which a freebet becomes eligible for cashout. Players must reach at least this X value to use the freebet."
        placement="top"
      >
        <Box className={Styles.Input}>
          <RHFTextField name="minMultiplier" label="Min Multiplier*" disabled={isFormDisabled} />
        </Box>
      </Tooltip>
      <Stack className={Styles.Block}>
        <Stack className={Styles.EligibilityColumn}>
          <strong>Player Eligibility Rule</strong>
          <Tooltip
            title="Sets the minimum balance a player must have to be eligible for receiving a rain distribution. Only players meeting this balance requirement will be included when the campaign runs."
            placement="top"
          >
            <Box className={Styles.EligibilityInput}>
              <Controller
                name="playerMinBalance"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Player Min Balance"
                    fullWidth
                    type="number"
                    value={field.value ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        field.onChange(null);
                      } else {
                        const numValue = Number(value);
                        field.onChange(Number.isNaN(numValue) ? value : numValue);
                      }
                    }}
                    error={!!error}
                    helperText={error?.message}
                    disabled={isFormDisabled}
                    sx={{
                      '& .MuiInputBase-root.Mui-disabled': {
                        backgroundColor: '#f5f5f5',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e0e0e0',
                        },
                      },
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: '#9e9e9e',
                        cursor: 'not-allowed',
                      },
                      '& .MuiInputLabel-root.Mui-disabled': {
                        color: '#9e9e9e',
                      },
                    }}
                  />
                )}
              />
            </Box>
          </Tooltip>
        </Stack>
        <Stack className={Styles.SetupWagerColumn}>
          <strong>Setup Wager</strong>
          <Stack className={Styles.Row}>
            <Tooltip
              title="Sets the minimum amount a player must wager within the selected wager period to qualify for receiving a rain distribution."
              placement="top"
            >
              <Box className={Styles.SetupWagerInput}>
                <Controller
                  name="playerMinWager"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Player Min Wager"
                      fullWidth
                      type="number"
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          field.onChange(null);
                        } else {
                          const numValue = Number(value);
                          field.onChange(Number.isNaN(numValue) ? value : numValue);
                        }
                      }}
                      error={!!error}
                      helperText={error?.message}
                      disabled={isFormDisabled}
                      sx={{
                        '& .MuiInputBase-root.Mui-disabled': {
                          backgroundColor: '#f5f5f5',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e0e0e0',
                          },
                        },
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: '#9e9e9e',
                          cursor: 'not-allowed',
                        },
                        '& .MuiInputLabel-root.Mui-disabled': {
                          color: '#9e9e9e',
                        },
                      }}
                    />
                  )}
                />
              </Box>
            </Tooltip>
            <Tooltip
              title="Defines the time window used to evaluate player wagering activity (e.g., last 5m, 10m, 30m, 1h). Only players who meet the minimum wager amount within this period become eligible."
              placement="top"
            >
              <Box className={Styles.SetupWagerInput}>
                <RHFSelect
                  label="Wager Period"
                  name="wagerPeriod"
                  variant="outlined"
                  disabled={isFormDisabled}
                >
                  <MenuItemEx value="" name="Wager Period">
                    <em>None</em>
                  </MenuItemEx>
                  <MenuItemEx value={5} name="Wager Period">
                    5m
                  </MenuItemEx>
                  <MenuItemEx value={10} name="Wager Period">
                    10m
                  </MenuItemEx>
                  <MenuItemEx value={30} name="Wager Period">
                    30m
                  </MenuItemEx>
                  <MenuItemEx value={60} name="Wager Period">
                    1h
                  </MenuItemEx>
                </RHFSelect>
              </Box>
            </Tooltip>
          </Stack>
        </Stack>
      </Stack>
      <strong>Campaign Budget</strong>
      <Stack className={Styles.Block}>
        <Tooltip
          title="Displays the total daily budget for this campaign. It is automatically calculated based on freebets per rain, number of daily rain events, and the value of each freebet."
          placement="top"
        >
          <Box className={Styles.Budgetinput}>
            <RHFTextField name="dayBudget" label="Daily Budget" disabled={true} />
          </Box>
        </Tooltip>
        <Tooltip
          title="Displays the total budget for the entire campaign. It is automatically calculated based on the daily budget and the campaign duration."
          placement="top"
        >
          <Box className={Styles.Budgetinput}>
            <RHFTextField name="totalBudget" label="Total Budget" disabled={true} />
          </Box>
        </Tooltip>
      </Stack>
    </Stack>
  );
};
